import {makeAutoObservable, observable, runInAction} from 'mobx';
import type {PublishBody} from '../../../data/api/StudioApi';
import StudioRepository from '../../../data/repositories/StudioRepository';
import {errorMessage} from '../errors';
import {studioText} from '../studioText';
import type {JobView, Publication} from '../types';
import {MutationScope, scopedIdentity} from './MutationScope';
import {studioRequest} from './studioRequests';

/** What the store holds; everything else is worked out from it and the job. */
export interface PublicationRecord {
  /** The last publication read or made, tagged with its job's id. */
  publication: Publication | null;
  publishingJobId: string | null;
  /** The job revision ("job1/hash") whose publication was read. */
  loaded: string | null;
  error: string | null;
}

/** Whether a job's current render is shared, worked out from the record and the job. */
export function publicationState(
  record: PublicationRecord,
  id: string,
  view: JobView | null,
) {
  const done = view?.job.status === 'done';
  const renderCount = view?.job.renderCount;
  const versionHash = view?.job.storyboard?.versionHash;
  const publishing = record.publishingJobId === id;
  const publication =
    record.publication?.jobId === id ? record.publication : null;
  const courseId = publication?.courseId || view?.job.publishedCourseId || null;
  const published =
    !!publication?.published &&
    (!publication.versionHash || publication.versionHash === versionHash) &&
    (publication.renderCount == null ||
      publication.renderCount === renderCount);
  // Knowledge check / attestation problems never block rendering, only publishing.
  const blockers = view?.publishBlockers || [];
  const previousRevisionId =
    publication && 'activeRevisionId' in publication
      ? publication.activeRevisionId ?? null
      : publication?.published === true
      ? publication.revisionId ?? null
      : null;
  // The live revision must be known before publishing replaces it.
  const ready = record.loaded === `${id}/${versionHash || ''}`;
  const canPublish =
    !!view && done && !view.running && !publishing && !blockers.length && ready;
  return {
    publication,
    courseId,
    published,
    publishing,
    ready,
    canPublish,
    blockers,
    error: record.error,
    previousRevisionId,
  };
}

/**
 * Whether the open job's current render is shared with learners, and sharing it. Each render
 * already saves the reels and their endings to the job's microreel group as an unshared draft;
 * publishing shares that group, which learners and courses are then given.
 *
 * A page calls `sync(organizationId, id, view)` whenever its job view changes (it reads the
 * publication once the job is done, and again for each new render), `close()` when it goes
 * away, and reads the state from `state`. Registered in storesProvider as
 * `studioPublicationStore`.
 */
export default class PublicationStore implements PublicationRecord {
  publication: Publication | null = null;
  publishingJobId: string | null = null;
  loaded: string | null = null;
  error: string | null = null;

  organizationId: string | undefined = undefined;
  jobId = '';
  view: JobView | null = null;

  private repository = new StudioRepository();
  private resetKey: string | null = null;
  private lookupKey: string | null = null;
  private refresh = 0;
  // The lookup whose answer may still be shown, and the newest request (a publish counts).
  private lookup = 0;
  private request = 0;
  private scope = new MutationScope();

  constructor() {
    makeAutoObservable<
      PublicationStore,
      | 'repository'
      | 'resetKey'
      | 'lookupKey'
      | 'refresh'
      | 'lookup'
      | 'request'
      | 'scope'
    >(this, {
      publication: observable.ref,
      view: observable.ref,
      repository: false,
      resetKey: false,
      lookupKey: false,
      refresh: false,
      lookup: false,
      request: false,
      scope: false,
    });
  }

  /** The open job's publication state; see `publicationState`. */
  get state() {
    return publicationState(this, this.jobId, this.view);
  }

  /** Follows the page's job: call on every new view. */
  sync(organizationId: string | undefined, id: string, view: JobView | null) {
    const renderCount = view?.job.renderCount;
    const versionHash = view?.job.storyboard?.versionHash;
    this.organizationId = organizationId;
    this.jobId = id;
    this.view = view;
    this.scope.setIdentity(
      scopedIdentity(organizationId, `${id}/${versionHash || ''}`),
    );
    this.scope.activate();
    const resetKey = JSON.stringify([id, renderCount, versionHash]);
    if (resetKey !== this.resetKey) {
      this.resetKey = resetKey;
      this.error = null;
      this.loaded = null;
    }
    this.lookUp();
  }

  /** The page went away: late answers are ignored and the next sync starts afresh. */
  close() {
    this.scope.deactivate();
    this.lookup += 1;
    this.lookupKey = null;
    this.resetKey = null;
    this.publication = null;
    this.publishingJobId = null;
    this.loaded = null;
    this.error = null;
  }

  /**
   * Shares the current render, then calls `reload` (the job store's) so the page shows the
   * shared job. Resolves to the publication, or null when it failed (see `state.error`) or the
   * page moved on.
   */
  async publish(reload?: () => Promise<unknown>): Promise<Publication | null> {
    const {view, jobId: id, organizationId} = this;
    const {canPublish, published, previousRevisionId} = this.state;
    if (!view || !canPublish || published) return null;
    const current = this.scope.begin();
    runInAction(() => {
      this.publishingJobId = id;
      this.error = null;
    });
    this.request += 1;
    try {
      const result = await this.sendPublish(organizationId, id, {
        name: view.job.brief.title,
        versionHash: view.job.storyboard?.versionHash,
        expectedStoreVersion: view.job.storeVersion,
        expectedPreviousRevisionId: previousRevisionId,
      });
      if (!current()) return null;
      if (!result?.published) throw new Error(studioText.shareFailed);
      const saved = {...result, jobId: id};
      runInAction(() => {
        this.publication = saved;
      });
      await reload?.();
      return current() ? saved : null;
    } catch (e) {
      if (current())
        runInAction(() => {
          this.error = errorMessage(e);
        });
      return null;
    } finally {
      runInAction(() => {
        if (this.publishingJobId === id) this.publishingJobId = null;
      });
    }
  }

  /** Reads the publication again (and the job, through `reload`) after a failure. */
  retry(reload?: () => Promise<unknown>) {
    runInAction(() => {
      this.error = null;
      this.refresh += 1;
    });
    reload?.();
    runInAction(() => this.lookUp());
  }

  /** Reads the publication of a done job, once for each render of each revision. */
  private lookUp() {
    const {organizationId, jobId: id, view} = this;
    const done = view?.job.status === 'done';
    const identity = `${id}/${view?.job.storyboard?.versionHash || ''}`;
    const key = JSON.stringify([
      id,
      done,
      view?.job.renderCount,
      identity,
      this.refresh,
    ]);
    if (key === this.lookupKey) return;
    this.lookupKey = key;
    const lookup = ++this.lookup;
    if (!done) return;
    const request = ++this.request;
    this.readPublication(organizationId, id)
      .then(result => {
        if (lookup === this.lookup && request === this.request)
          runInAction(() => {
            this.publication = result ? {...result, jobId: id} : null;
            this.loaded = identity;
          });
      })
      .catch(e => {
        if (lookup === this.lookup)
          runInAction(() => {
            this.error = errorMessage(e);
          });
      });
  }

  private readPublication(organizationId: string | undefined, id: string) {
    return studioRequest(organizationId, org =>
      this.repository.publication(org, id),
    );
  }

  private sendPublish(
    organizationId: string | undefined,
    id: string,
    body: PublishBody,
  ) {
    return studioRequest(organizationId, org =>
      this.repository.publish(org, id, body),
    );
  }
}
